import { type Registry as Services, service } from '@ember/service';
import BaseRepositoryService from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { HomeawayConnectionValidation } from 'uplisting-frontend/validations/homeaway-connection';

const CHANNEL_CONNECTIONS_PERMISSION = 'connect.channel_connections.view';

type HomeawayConnection = ModelRegistry['homeaway-connection'];
type HomeawayLink = ModelRegistry['homeaway-link'];

export default class HomeawayConnectionRepositoryService extends BaseRepositoryService<HomeawayConnection> {
  @service session!: Services['session'];

  recordName = 'homeaway-connection';
  implementMethods = ['buildChangeset', 'findAll', 'unloadAll'];

  validation = HomeawayConnectionValidation;

  public async findAllForLinking(): Promise<HomeawayConnection[]> {
    if (
      !this.session.currentUser?.hasAccessTo(CHANNEL_CONNECTIONS_PERMISSION)
    ) {
      return [];
    }

    try {
      const connections = await this.store.findAll('homeaway-connection', {
        reload: true,
      });

      return connections.slice();
    } catch (error) {
      this.sentry.captureException(error);

      return [];
    }
  }

  public namedConnections(
    connections: HomeawayConnection[],
  ): HomeawayConnection[] {
    return connections.filter((connection) => !!connection.accountName);
  }

  public assignDefaultConnection(
    link: HomeawayLink,
    connections: HomeawayConnection[],
  ): void {
    if (link.id || link.homeawayConnectionId) {
      return;
    }

    const [onlyConnection, ...otherConnections] =
      this.namedConnections(connections);

    if (onlyConnection && otherConnections.length === 0) {
      link.homeawayConnectionId = Number(onlyConnection.id);
    }
  }

  public isConnectionChoiceRequired(
    link: HomeawayLink,
    connections: HomeawayConnection[],
  ): boolean {
    return (
      !link.id &&
      !link.homeawayConnectionId &&
      this.namedConnections(connections).length > 1
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    'repositories/homeaway-connection': HomeawayConnectionRepositoryService;
  }
}
